'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import { InterfaceChainUtils } from '@morpho-org/dapp-core';
import { Avatar } from '@morpho-org/ui-v6/client';
import { formatLltv, formatMarketName, formatters } from '@morpho-org/ui-v6/utils';
import { TruncatedText } from '@repo/ui';
import { InternalLink } from '@/components/common/InternalLink';
import { getMarketUrl } from '@/helpers/routing';
import type { MarketOracles } from '../oracles.contract';
import { CardTrigger } from '../OracleFeeds/OracleFeedParts';
type UsageMarket = NonNullable<MarketOracles['oracle']>['markets'][number];
const TITLE = 'Value secured';

/** Same as the curator card's deposits. */
const usdShort = formatters.short.unit('$').digits(2);

/** Zero reads `$0` and dust `<$0.01`, not the short formatter's `$0.00` and `$< 0.01`. */
const formatUsd = (value: number) => value === 0 ? '$0' : value > 0 && value < 0.01 ? '<$0.01' : usdShort.of(value);

/** The vault allocation tooltip's market row: loan then collateral logo, name, LLTV chip, value. */
function MarketRow(t0) {
  const $ = _c(35);
  const {
    chainId,
    market
  } = t0;
  const {
    collateralToken,
    loanToken
  } = market;
  const t1 = collateralToken ? collateralToken.symbol ?? "" : null;
  let t2;
  if ($[0] !== loanToken.symbol || $[1] !== t1) {
    t2 = formatMarketName(loanToken.symbol, t1);
    $[0] = loanToken.symbol;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const name = t2;
  let t3;
  if ($[3] !== chainId || $[4] !== collateralToken?.symbol || $[5] !== loanToken.symbol || $[6] !== market.marketId) {
    t3 = getMarketUrl({
      identifier: InterfaceChainUtils.getIdentifierById(chainId),
      marketId: market.marketId,
      collateralSymbol: collateralToken?.symbol ?? "",
      loanSymbol: loanToken.symbol ?? ""
    });
    $[3] = chainId;
    $[4] = collateralToken?.symbol;
    $[5] = loanToken.symbol;
    $[6] = market.marketId;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== loanToken.logoUri) {
    t4 = <Avatar src={loanToken.logoUri} size={16} padding={0} rounded={true} aria-hidden={true} />;
    $[8] = loanToken.logoUri;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== collateralToken) {
    t5 = collateralToken && <Avatar src={collateralToken.logoUri} size={16} padding={0} rounded={true} aria-hidden={true} className="-ml-[10px]" />;
    $[10] = collateralToken;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <span className="flex shrink-0 items-center">{t4}{t5}</span>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== name) {
    t7 = <TruncatedText tag="span" size="2xs" className="text-foreground min-w-0" tabIndex={-1}>{name}</TruncatedText>;
    $[15] = name;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== market.lltv) {
    t8 = formatLltv(market.lltv);
    $[17] = market.lltv;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t8) {
    t9 = <span className="bg-muted text-secondary-foreground shrink-0 rounded-[10px] px-[6px] py-px text-[10px]">{t8}</span>;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t6 || $[22] !== t7 || $[23] !== t9) {
    t10 = <div className="gap-xs flex min-w-0 items-center">{t6}{t7}{t9}</div>;
    $[21] = t6;
    $[22] = t7;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== market.valueUsd) {
    t11 = formatUsd(market.valueUsd);
    $[25] = market.valueUsd;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t11) {
    t12 = <span className="text-2xs text-foreground whitespace-nowrap">{t11}</span>;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t10 || $[30] !== t12) {
    t13 = <div className="gap-sm px-xs py-2xs hover:bg-background flex w-full cursor-pointer items-center justify-between rounded-lg">{t10}{t12}</div>;
    $[29] = t10;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t13 || $[33] !== t3) {
    t14 = <InternalLink href={t3} onClick={_temp}>{t13}</InternalLink>;
    $[32] = t13;
    $[33] = t3;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  return t14;
}

/**
 * The oracle's value secured and, when several markets share the oracle, how many, opening the
 * per-market breakdown on hover (a bottom sheet on touch screens).
 */
function _temp(event) {
  return event.stopPropagation();
}
export function ValueSecured(t0) {
  const $ = _c(13);
  const {
    chainId,
    valueSecured,
    oracle
  } = t0;
  let t1;
  if ($[0] !== oracle) {
    t1 = oracle?.valueSecuredUsd == null ? [] : oracle.markets;
    $[0] = oracle;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const markets = t1;
  if (markets.length <= 1) {
    let t2;
    if ($[2] !== valueSecured) {
      t2 = <span className="text-foreground text-xs">{valueSecured}</span>;
      $[2] = valueSecured;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  const t2 = `${valueSecured}, show the markets using this oracle`;
  let t3;
  if ($[4] !== chainId || $[5] !== markets) {
    let t4;
    if ($[7] !== chainId) {
      t4 = market => <MarketRow key={market.marketId} chainId={chainId} market={market} />;
      $[7] = chainId;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    t3 = markets.map(t4);
    $[4] = chainId;
    $[5] = markets;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[9] !== t2 || $[10] !== t3 || $[11] !== valueSecured) {
    t4 = <CardTrigger title={TITLE} aside={valueSecured} label={t2} className="text-foreground text-xs" rows={t3}>{valueSecured}</CardTrigger>;
    $[9] = t2;
    $[10] = t3;
    $[11] = valueSecured;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}